import { useCallback } from "react";

import { generatePath, useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { InventoryChangeReason } from "__graphql__/types";
import { PageName, StockCorrectionsOrigin } from "analytics/events";
import { routes } from "config/routes";
import { useRestockingServiceStore } from "flows/Inventory/flows/RestockingList/stores/restockingServiceStore";
import { InventoryProduct } from "flows/Inventory/shared/models/inventoryProduct/types";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";

import { UpdateProductStockSuccessPayload } from "./types";
import { useStockCorrectionServices } from "./useStockCorrectionServices";
import { useStockCorrectionStore } from "./useStockCorrectionStore";

const EAN_NOT_FOUND_TOAST_ID = "ean_not_found_toast_id";
const NO_PRODUCT_ASSIGNED_TOAST_ID = "no_product_assigned_toast_id";
const STOCK_UPDATE_SUCCESS_TOAST_ID = "stock_update_success_toast_id";
const STOCK_UPDATE_ERROR_TOAST_ID = "stock_update_error_toast_id";

const getState = () => useStockCorrectionStore.getState();

export function useStockCorrectionActions() {
  const { showToastUI } = useCustomToast();
  const navigate = useNavigate();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { fetchProducts, fetchProductsByText, updateProductStock } = useStockCorrectionServices();

  const { setUseRestockingServiceStore, setPrivateRestockingListRefreshNeeded } =
    useRestockingServiceStore(
      useShallow((state) => ({
        setUseRestockingServiceStore: state.setUseRestockingServiceStore,
        setPrivateRestockingListRefreshNeeded: state.setPrivateRestockingListRefreshNeeded,
      })),
    );

  // --- Toast actions ---

  const showScanProductError = useCallback(() => {
    showToastUI({ id: EAN_NOT_FOUND_TOAST_ID, title: "hooks.use-stock-correction.ean-not-found" });
  }, [showToastUI]);

  const showNoProductAssignedError = useCallback(() => {
    showToastUI({
      id: NO_PRODUCT_ASSIGNED_TOAST_ID,
      title: "hooks.use-stock-correction.no-product-assigned",
    });
  }, [showToastUI]);

  const showUpdateProductStockError = useCallback(() => {
    showToastUI({
      id: STOCK_UPDATE_ERROR_TOAST_ID,
      title: "hooks.use-stock-correction.stock-update-error",
    });
  }, [showToastUI]);

  const showUpdateProductStockSuccess = useCallback(() => {
    showToastUI({
      id: STOCK_UPDATE_SUCCESS_TOAST_ID,
      status: "success",
      title: "hooks.use-stock-correction.stock-updated",
    });
  }, [showToastUI]);

  // --- Analytics ---

  const sendStartCorrectionsEvent = useCallback(() => {
    const state = getState();
    let origin: StockCorrectionsOrigin = "scanned_product";
    if (state.scanType === "shelf") origin = "scanned_shelf";
    if (state.scanType === "search") origin = "search_bar";
    const proposalId = state.originInfo?.proposalId;
    sendSegmentTrackEvent("stockCorrectionStarted", {
      stock_correction_id: state.stockCorrectionId as string,
      product_sku: state.selectedInventoryProductSku as string,
      shelf_number: state.selectedInventoryProductShelf ?? "none",
      origin,
      screen_name: "stock_correction_screen",
      ...(proposalId ? { shelf_change_id: proposalId } : {}),
    });
  }, [sendSegmentTrackEvent]);

  const sendFinishCorrectionsEvent = useCallback(
    (payload: UpdateProductStockSuccessPayload) => {
      const state = getState();
      const proposalId = state.originInfo?.proposalId;
      sendSegmentTrackEvent("stockCorrectionFinished", {
        stock_correction_id: state.stockCorrectionId as string,
        screen_name: "stock_correction_screen",
        product_sku: state.selectedInventoryProductSku as string,
        shelf_number: state.selectedInventoryProductShelf ?? "none",
        quantity_before_correction: payload.before,
        quantity_after_correction: payload.after,
        reason: payload.reason ?? undefined,
        quantity: payload.counter,
        ...(proposalId ? { shelf_change_id: proposalId } : {}),
      });
    },
    [sendSegmentTrackEvent],
  );

  const sendStartCorrectionsByShortcutEvent = useCallback(() => {
    const state = getState();
    sendSegmentTrackEvent("stockCorrectionStarted", {
      stock_correction_id: state.stockCorrectionId as string,
      product_sku: state.selectedInventoryProductSku as string,
      shelf_number: state.selectedInventoryProductShelf ?? "none",
      origin: state.scanType === "product" ? "scanned_product" : "scanned_shelf",
      screen_name: "stock_correction_multiple_results",
    });
  }, [sendSegmentTrackEvent]);

  const sendFinishCorrectionsByShortcutEvent = useCallback(
    (stock: number) => {
      const state = getState();
      sendSegmentTrackEvent("stockCorrectionFinished", {
        stock_correction_id: state.stockCorrectionId as string,
        product_sku: state.selectedInventoryProductSku as string,
        shelf_number: state.selectedInventoryProductShelf ?? "none",
        quantity_before_correction: stock,
        quantity_after_correction: stock,
        screen_name: "stock_correction_multiple_results",
      });
    },
    [sendSegmentTrackEvent],
  );

  // --- Navigation helpers ---

  const resetRestockingAndNavigateBack = useCallback(() => {
    const currentPageName = useRestockingServiceStore.getState().pageName;
    const path =
      currentPageName === PageName.INVENTORY_PAGE_DROP_OFF
        ? routes.inventory.restockingList.dropOff
        : routes.inventory.restockingList.root;

    setUseRestockingServiceStore({
      isNavigatingToStockCorrection: false,
      productSku: null,
      pageName: null,
    });

    setTimeout(() => navigate(path, { replace: true }), 0);
  }, [navigate, setUseRestockingServiceStore]);

  const navigateBackToShelfChanges = useCallback(() => {
    const state = getState();
    const url = state.originInfo?.proposalId
      ? generatePath(routes.inventory.shelfChanges.detail, {
          proposalId: state.originInfo.proposalId,
        })
      : routes.inventory.shelfChanges.root;

    setTimeout(() => navigate(url), 0);
  }, [navigate]);

  // --- Async flows ---

  const scan = useCallback(
    async (barcode: string) => {
      getState().resetInventoryProductsList();
      getState().setPhase("resolvingProducts");
      try {
        const context = getState();
        const products = await fetchProducts(barcode);

        if (
          context.scanType === "shelf" &&
          (products.every((p) => p.shelf !== context.scannedValue) || products.length === 0)
        ) {
          showNoProductAssignedError();
          getState().enterIdle();
          return;
        }

        if (products.length === 0) {
          showScanProductError();
          getState().enterIdle();
          return;
        }
        getState().setInventoryProductsList(products);

        if (products.length > 1) {
          getState().setPhase("selectProduct");
          navigate(routes.inventory.stockCorrections.selectProduct);
        } else {
          const first = products[0];
          if (first) {
            getState().setInventoryProduct(first.productSku, first.shelf);
            getState().setStockCorrectionId();
            getState().setPhase("declareStockCorrections");
            sendStartCorrectionsEvent();
            navigate(routes.inventory.stockCorrections.declare);
          } else {
            showScanProductError();
            getState().enterIdle();
          }
        }
      } catch {
        showScanProductError();
        getState().enterIdle();
      }
    },
    [
      fetchProducts,
      navigate,
      sendStartCorrectionsEvent,
      showNoProductAssignedError,
      showScanProductError,
    ],
  );

  const scanProductFromText = useCallback(
    async (query: string) => {
      getState().resetInventoryProductsList();
      getState().setPhase("searchLoading");
      try {
        const products = await fetchProductsByText(query);
        const first = products[0];
        if (first) {
          getState().setInventoryProductsList(products);
          getState().setInventoryProduct(first.productSku, first.shelf);
          getState().setStockCorrectionId();
          getState().setPhase("declareStockCorrections");
          sendStartCorrectionsEvent();
        }
      } catch {
        showScanProductError();
        getState().setPhase("declareStockCorrections");
      }
    },
    [fetchProductsByText, sendStartCorrectionsEvent, showScanProductError],
  );

  const searchProducts = useCallback(
    async (query: string) => {
      getState().resetInventoryProductsList();
      getState().setPhase("searchLoading");
      try {
        const products = await fetchProductsByText(query);
        getState().setInventoryProductsList(products);
        getState().setPhase("search");
      } catch {
        getState().setPhase("search");
      }
    },
    [fetchProductsByText],
  );

  const submitStockUpdate = useCallback(
    async (event: {
      initial: number;
      reason: InventoryChangeReason | null;
      counter: number;
      newTotalStock: number;
    }) => {
      getState().setPhase("declareStockCorrectionsLoading");
      try {
        const context = getState();
        const result = await updateProductStock(context, event);

        sendFinishCorrectionsEvent(result);
        getState().updateInventoryProductStock(result);
        showUpdateProductStockSuccess();

        const isFromRestocking = useRestockingServiceStore.getState().isNavigatingToStockCorrection;
        const isFromShelfChanges =
          context.originInfo?.originPage === PageName.INVENTORY_SHELF_CHANGES_PAGE;

        if (isFromRestocking) {
          resetRestockingAndNavigateBack();
          setPrivateRestockingListRefreshNeeded(true);
          getState().reset();
          return;
        }

        if (isFromShelfChanges) {
          navigateBackToShelfChanges();
          getState().reset();
          return;
        }

        if (context.scanType === "shelf") {
          getState().setPhase("selectProduct");
          navigate(routes.inventory.stockCorrections.selectProduct);
          return;
        }

        // Default: reset and go back to idle
        getState().reset();
        navigate(routes.inventory.stockCorrections.root, { replace: true });
      } catch {
        showUpdateProductStockError();
        getState().reset();
        navigate(routes.inventory.stockCorrections.root, { replace: true });
      }
    },
    [
      navigate,
      navigateBackToShelfChanges,
      resetRestockingAndNavigateBack,
      sendFinishCorrectionsEvent,
      setPrivateRestockingListRefreshNeeded,
      showUpdateProductStockError,
      showUpdateProductStockSuccess,
      updateProductStock,
    ],
  );

  // --- Compound actions for pages ---

  const activateSearch = useCallback(() => {
    getState().resetInventoryProductsList();
    getState().setScanType("search");
    getState().setPhase("search");
  }, []);

  const cancelSearch = useCallback(() => {
    getState().resetInventoryProductsList();
    getState().enterIdle();
  }, []);

  const resetSearchResults = useCallback(() => {
    getState().resetInventoryProductsList();
  }, []);

  const selectProductAndDeclare = useCallback(
    (sku: string, shelf: string | null) => {
      getState().setInventoryProduct(sku, shelf);
      getState().setStockCorrectionId();
      getState().setPhase("declareStockCorrections");
      sendStartCorrectionsEvent();
      navigate(routes.inventory.stockCorrections.declare);
    },
    [navigate, sendStartCorrectionsEvent],
  );

  const validateProductStock = useCallback(
    (product: InventoryProduct) => {
      getState().setInventoryProduct(product.productSku, product.shelf);
      getState().validateInventoryProductStock(product.productSku);
      sendStartCorrectionsByShortcutEvent();
      sendFinishCorrectionsByShortcutEvent(product.stock);
    },
    [sendFinishCorrectionsByShortcutEvent, sendStartCorrectionsByShortcutEvent],
  );

  const clearListAndGoIdle = useCallback(() => {
    getState().reset();
    navigate(routes.inventory.stockCorrections.root, { replace: true });
  }, [navigate]);

  const goBackToIdle = useCallback(() => {
    getState().enterIdle();
    getState().resetOriginInfo();
    setUseRestockingServiceStore({
      isNavigatingToStockCorrection: false,
      productSku: null,
      pageName: null,
    });
    navigate(-1);
  }, [navigate, setUseRestockingServiceStore]);

  return {
    scan,
    scanProductFromText,
    searchProducts,
    submitStockUpdate,
    activateSearch,
    cancelSearch,
    resetSearchResults,
    selectProductAndDeclare,
    validateProductStock,
    clearListAndGoIdle,
    goBackToIdle,
  };
}
